import Button from "@components/common/Button";
import { ModalCreditLimitHistory } from "@components/Modals/CreditLimit/ModalCreditLimitHistory";
import { ModalConfirmationTopUp } from "@components/Modals/ModalConfirmationTopUp";
import { fetchStarlinkDetails, fetchVessel, setHandleCreditLimit } from "@services/vesselService";
import { setVesselDetails } from "@store/modules/vesselsSlice";
import { checkPermission } from "@utils/permissions";
import { Suspense, startTransition, useEffect, useRef, useState } from "react";
import Chart from "react-apexcharts";
import { FaInfinity } from "react-icons/fa";
import { useSelector } from "react-redux";

const CreditLimitDonutChart = ({
	id,
	typeoptin,
	allowanceValue,
	allowanceCurrentValue,
	alerts,
	totalUsageThisMonth,
	unlimitedValue,
	lengthalerts,
	kitNumber,
	histories,
	vesselid,
	usageData,
	token,
	navigate,
	dispatch,
	kitdata,
}) => {
	const emptyAlerts = lengthalerts - alerts.length + 1;
	const chartRef = useRef(null); // Reference to the chart container
	const [iconSize, setIconSize] = useState(24); // State for the icon size
	const [openModal, setOpenModal] = useState(false);
	const [openModalConfirmationTopUp, setOpenModalConfirmationTopUp] = useState(null);
	const allowanceNum = parseFloat(allowanceValue);
	const allowanceCurrentNum = parseFloat(allowanceCurrentValue);
	const usagePercentage = ((allowanceCurrentNum / allowanceNum) * 100).toFixed(2);

	const { permissions } = useSelector((state) => state.auth.user);
	const vesselsSite = permissions.find((resource) => resource.resource_name === "vessels_site");
	const vesselStarlinks = vesselsSite?.providers?.find(
		(subResource) => subResource.sub_resource_name === "modules",
	);

	const starlink = vesselsSite?.providers?.find(
		(subResource) => subResource.sub_resource_name === "starlink",
	);
	const authorizedPermissions = vesselStarlinks?.permissions?.filter(
		(permission) => permission.authorized === true,
	);
	const authorizedPermissionsStarlink = starlink?.permissions?.filter(
		(permission) => permission.authorized === true,
	);
	const canViewActivationProv = checkPermission(authorizedPermissions, "view activation prov");

	const canEditCreditLimit = checkPermission(authorizedPermissionsStarlink, "edit credit limit");
	// Dynamically adjust the icon size based on the chart's height
	useEffect(() => {
		if (chartRef.current) {
			const height = chartRef.current.offsetHeight;
			setIconSize(Math.max(height * 0.15, 24)); // 15% of the height, minimum size of 24px
		}
	}, []);

	const options = {
		chart: {
			type: "donut",
			height: 270, // Base height for large screens
		},
		labels:
			typeoptin === "OPT_OUT" && Number(allowanceValue) === 0
				? ["Allowance current"]
				: ["Allowance current", "Remaining"],
		colors:
			typeoptin === "OPT_OUT" && Number(allowanceValue) === 0
				? ["#1B204C"]
				: ["#0E9F6E", "#1B204C"],
		plotOptions: {
			pie: {
				donut: {
					size: "65%", // Default donut size
					labels: {
						show: true,
						value: {
							formatter: (val) => {
								return `${parseFloat(val).toFixed(2)}%`;
							},
						},
						total: {
							show: true,
							label: typeoptin === "UNLIMITED" ? "" : "Allowance usage",
							formatter: () => {
								if (typeoptin === "OPT_OUT" && Number(allowanceValue) === 0) {
									return "N/A";
								}
								if (typeoptin === "UNLIMITED") {
									return "";
								}
								return `${parseFloat(usagePercentage).toFixed(2)} %`;
							},
						},
					},
				},
				expandOnClick: false,
			},
		},
		stroke: {
			width: 0.5, // Reduce stroke width for a smaller border
			colors: ["transparent"], // Set to transparent for no visible gap
		},
		tooltip: {
			theme: "dark", // Preset dark theme (optional)
			style: {
				fontSize: "14px",
				fontFamily: "Arial, sans-serif",
			},
			enabled: typeoptin !== "OPT_OUT", // Disable tooltip if typeoptin is "OPT_OUT"
			y: {
				formatter: (val) => `${val.toFixed(2)} %`, // Format tooltip values
			},
			custom: ({
				series,
				seriesIndex,
				w,
			}) => `<div style="background:#272B49 ; color: #FFFFFF; padding: 8px; border-radius: 6px;">
                  <span class="font-semibold text-base leading-normal tracking-normal" style="font-family: 'Segoe UI', sans-serif; color:#9CA3AF"  >${w.globals.labels[seriesIndex]}:</span>  <span  class="font-bold text-sm leading-none tracking-normal" style="font-family: 'Segoe UI', sans-serif;" >  ${series[seriesIndex].toFixed(2)}%</span>
                </div>`,
		},
		dataLabels: {
			enabled: false,
		},
		legend: {
			position: "bottom",
			horizontalAlign: "center",
			labels: {
				colors: ["#ffffff"],
			},
		},
	};
	const series =
		typeoptin === "OPT_OUT" && Number(allowanceValue) === 0
			? [100]
			: [
					parseFloat(parseFloat(usagePercentage).toFixed(2)),
					parseFloat((100 - parseFloat(usagePercentage)).toFixed(2)),
				];

	// const [initialCreditLimitData, setInitialCreditLimitData] = useState({
	//     currentMonthOption: "OPT_OUT",
	//     currentMonthValue: "",
	//     permanentOption: "OPT_OUT",
	//     permanentValue: "",
	//   });

	//   const [creditLimitData, setCreditLimitData] = useState(
	//     initialCreditLimitData
	//   );

	const handleTopUp = async () => {
		const currentValue = parseFloat(kitdata.credit_limit.current_month_value || "0");

		try {
			const formData = new FormData();
			formData.append("starlink_id", kitdata?.id);
			formData.append("monthly_type", "WITH_CREDIT_LIMIT");
			formData.append("monthly_value", (currentValue + 50).toFixed(2));
			formData.append("top_up_allowance", parseInt(kitdata?.credit_limit?.top_up_value) + 50);

			formData.append("permanent_type", kitdata?.credit_limit?.permanent_option || "OPT_OUT");

			// formData.append(
			//   "permanent_value",
			//   kitdata.credit_limit.permanent_value || ""
			// );

			// if (creditLimitData.permanentOption === kitdata.credit_limit.permanent_option || "OPT_OUT") {

			// }

			const result = await setHandleCreditLimit(formData, token, dispatch, navigate);

			const fieldLabels = {
				monthly_value: "Monthly Limit",
				permanent_value: "Permanent Limit",
				monthly_type: "Monthly Type",
				permanent_type: "Permanent Type",
				starlink_id: "Starlink Kit",
			};

			if (result?.errors) {
				const errorMessages = [];

				Object.entries(result.errors).forEach(([key, messages]) => {
					const label =
						fieldLabels[key] || key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
					messages.forEach((msg) => {
						errorMessages.push(`${label}: ${msg}`);
					});
				});

				setErrorMessage(errorMessages.join("\n"));
				setShowErrorModal(true);
			} else if (result?.status === 200) {
				const data = await fetchVessel(token, vesselid, dispatch, navigate);
				startTransition(() => {
					dispatch(setVesselDetails(data));
				});

				setSuccessMessage(result.message || "Opt-in Successful");
				setShowSuccessModal(true);
			} else {
				setErrorMessage(result?.message || "Something went wrong. Please try again.");
				setShowErrorModal(true);
			}
		} catch (error) {
			console.error("Error", error);
			setErrorMessage("An error occurred. Please try again.");
			setShowErrorModal(true);
		} finally {
			// setLoaderSaveCreditLimit(false);
		}
	};
	return (
		<div key={id} className="relative">
			<div className="relative" ref={chartRef}>
				<div style={{ padding: "20px" }}></div>

				<div style={{ height: "270px" }}>
					<Chart options={options} series={series} type="donut" height={270} />{" "}
				</div>
				{typeoptin === "UNLIMITED" && (
					<div className="absolute inset-0 flex items-center justify-center">
						<FaInfinity style={{ fontSize: `${iconSize}px` }} className="text-gray-300" />
					</div>
				)}
			</div>

			<div
				className="col-span-2 rounded-2xl bg-blue-gray bg-opacity-20 p-4"
				style={{
					paddingBottom: `${Number.isInteger(emptyAlerts) && emptyAlerts > 0 ? 28 * emptyAlerts + 16 : 16}px`,
				}}
			>
				<div className="mb-2 flex justify-between">
					<span className="font-normal text-sm text-white leading-tight">
						Extra Allowance value
					</span>
					<span className="me-2 rounded bg-blue-gray px-2.5 py-0.5 font-medium text-white text-xs">
						{(typeoptin === "OPT_OUT" || typeoptin === "WITH_CREDIT_LIMIT") && allowanceValue > 0
							? `${allowanceValue} GB`
							: typeoptin === "UNLIMITED"
								? "Unlimited"
								: "N/A"}
					</span>
				</div>
				<div className="mb-2 flex justify-between">
					<span className="font-normal text-sm text-white leading-tight">
						Extra Allowance current value
					</span>
					<span className="me-2 rounded bg-green-cyan px-2.5 py-0.5 font-medium text-white text-xs">
						{typeoptin === "OPT_OUT" || typeoptin === "WITH_CREDIT_LIMIT"
							? `${allowanceCurrentValue} GB`
							: typeoptin === "UNLIMITED"
								? unlimitedValue < 0
									? `${Math.abs(unlimitedValue).toFixed(2)} GB`
									: "N/A"
								: "N/A"}
					</span>
				</div>
				{typeoptin === "OPT_OUT" && allowanceValue > 0 && (
					<div className="mb-2 flex justify-between">
						<span className="font-normal text-sm text-white leading-tight">
							Credit Limit Policy
						</span>
						<span className="me-2 rounded bg-custom-red px-2.5 py-0.5 font-medium text-white text-xs">
							OPT_OUT
						</span>
					</div>
				)}

				{/* <div className="mb-2 flex justify-between">
          <span className="text-gray-400 leading-tight text-sm font-normal">
            Total Usage
          </span>
          <span className="text-xs font-medium me-2 px-2.5 py-0.5 rounded bg-green-900 text-green-300">
            {`${totalUsageThisMonth} GB`}
          </span>
        </div> */}

				{alerts &&
					alerts.length > 0 &&
					alerts.map((alert, index) => (
						<div key={index} className="mb-2 flex justify-between">
							<span className="font-normal text-sm text-white leading-tight">
								{alert.threshold} %
							</span>
							<span
								className={`me-2 rounded px-2.5 py-0.5 font-medium text-xs ${
									alert.sent_at === null ? "bg-green-cyan text-white" : "bg-custom-red text-white"
								}`}
							>
								{alert.sent_at === null ? "Not yet" : new Date(alert.sent_at).toLocaleString()}
							</span>
						</div>
					))}
				<div className="ify-betweenjust mb-2 flex">
					{histories && histories.length > 0 && (
						<Button
							text={"Credit Limit History"}
							color="gray"
							onClick={() => {
								setOpenModal(true);
							}}
							className="me-2 mb-2 inline-flex items-center rounded-lg bg-gray-800 text-center font-medium text-sm text-white hover:bg-gray-900"
						/>
					)}
				</div>
				<div className="ify-betweenjust mb-2 flex">
					{canEditCreditLimit && (
						<>
							{usageData?.block_product === 1 && (
								<Button
									text={" Add Top Up"}
									color="gray"
									onClick={async () => {
										if (canViewActivationProv) {
											const url = `/vessel/${vesselid}/${usageData.id}?topup=true`;
											await fetchStarlinkDetails(
												usageData.id,
												usageData.kit_number,
												token,
												dispatch,
												navigate,
												url,
											);
											navigate(url); // navigate only after data is fetched
										} else {
											setOpenModalConfirmationTopUp(true);
										}
									}}
									className="me-2 mb-2 inline-flex items-center rounded-lg bg-custom-blue text-center font-medium text-sm text-white hover:opacity-80"
								/>
							)}
						</>
					)}
				</div>

				<ModalCreditLimitHistory
					isOpen={openModal}
					kitNumber={kitNumber}
					histories={histories}
					closeModal={() => setOpenModal(false)}
				/>

				{openModalConfirmationTopUp && (
					<Suspense fallback={<div></div>}>
						<ModalConfirmationTopUp
							isOpen={openModalConfirmationTopUp}
							closeModal={() => setOpenModalConfirmationTopUp(false)}
							onConfirm={() => {
								handleTopUp();
								setOpenModalConfirmationTopUp(false);
							}}
							setTopUpDirectly={true}
						/>
					</Suspense>
				)}
			</div>
		</div>
	);
};

export default CreditLimitDonutChart;
